import { z } from "zod";

/**
 * Client-side password policy shown in PasswordStrength and enforced by
 * register / reset / change-password forms. Stricter than the backend floor
 * (8 chars) so users get clear, consistent requirements before submit.
 */
export const PASSWORD_MIN_LENGTH = 10;

/** Mirrors PASSWORD_MAX_LENGTH in backend/app/core/validators.py. Without it a
 *  pasted passphrase clears the client check and comes back as a raw 422. */
export const PASSWORD_MAX_LENGTH = 128;

export const PASSWORD_REQUIREMENTS = [
  {
    id: "length",
    label: `${PASSWORD_MIN_LENGTH} Characters`,
    test: (p: string) =>
      p.length >= PASSWORD_MIN_LENGTH && p.length <= PASSWORD_MAX_LENGTH,
  },
  {
    id: "case",
    label: "Varying Case",
    test: (p: string) => /[a-z]/.test(p) && /[A-Z]/.test(p),
  },
  {
    id: "special",
    label: "Special Character",
    test: (p: string) => /[^A-Za-z0-9]/.test(p),
  },
] as const;

export type PasswordRequirement = (typeof PASSWORD_REQUIREMENTS)[number];

export function meetsPasswordRequirements(password: string): boolean {
  return PASSWORD_REQUIREMENTS.every((requirement) =>
    requirement.test(password),
  );
}

export function getPasswordStrength(password: string): {
  score: number;
  label: string;
  color: string;
  metCount: number;
  total: number;
} {
  const total = PASSWORD_REQUIREMENTS.length;
  if (!password) {
    return { score: 0, label: "", color: "", metCount: 0, total };
  }

  const metCount = PASSWORD_REQUIREMENTS.filter((r) => r.test(password)).length;

  // "Strong" is gated on every requirement passing rather than on a count, so
  // adding a fourth requirement cannot promote a password that still fails one
  // to Strong (it drops to "Good" instead) while submit stays disabled.
  if (metCount === total) {
    return {
      score: metCount,
      label: "Strong",
      color: "var(--strength-strong)",
      metCount,
      total,
    };
  }
  if (metCount <= 1) {
    return {
      score: metCount,
      label: "Weak",
      color: "var(--strength-weak)",
      metCount,
      total,
    };
  }
  if (metCount === 2) {
    return {
      score: metCount,
      label: "Fair",
      color: "var(--strength-fair)",
      metCount,
      total,
    };
  }
  return {
    score: metCount,
    label: "Good",
    color: "var(--strength-good)",
    metCount,
    total,
  };
}

export const passwordFieldSchema = z
  .string()
  .min(
    PASSWORD_MIN_LENGTH,
    `Password must be at least ${PASSWORD_MIN_LENGTH} characters`,
  )
  .max(
    PASSWORD_MAX_LENGTH,
    `Password must be at most ${PASSWORD_MAX_LENGTH} characters`,
  )
  .regex(/[A-Z]/, "Must contain an uppercase letter")
  .regex(/[a-z]/, "Must contain a lowercase letter")
  .regex(/[^A-Za-z0-9]/, "Must contain a special character");
